import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { isChunkLoadError, reloadOnStaleChunk } from '@/lib/chunkReload';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return String(error);
}

/**
 * React Router's data router (RouterProvider) catches render/lazy-import
 * errors internally and never lets them reach an ordinary React error
 * boundary further up the tree — it renders whatever errorElement is set on
 * the matched route instead (or its own "Unexpected Application Error!"
 * fallback if none is set). A lazy page failing to load after a deploy
 * (stale chunk hash) surfaces here, not as a window-level unhandledrejection,
 * so it needs its own reload handling distinct from main.tsx's listener.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const message = getErrorMessage(error);
  const isStaleChunk = isChunkLoadError(message);

  useEffect(() => {
    if (isStaleChunk) reloadOnStaleChunk();
  }, [isStaleChunk]);

  if (isStaleChunk) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-900 text-white/60">
        Updating to the latest version…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4 text-center">
      <div className="max-w-lg">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-500/20">
          <AlertTriangle className="h-10 w-10 text-red-400" />
        </div>
        <h1 className="mb-3 text-2xl font-bold text-white">This page hit a snag</h1>
        <p className="mb-8 text-white/70">
          Reloading usually fixes it. If it keeps happening, reach out and we'll take a look.
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button onClick={() => window.location.reload()} className="bg-accent-500 text-white hover:bg-accent-400">
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </Button>
          <Button
            onClick={() => { window.location.href = '/'; }}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Home className="mr-2 h-4 w-4" />
            Go home
          </Button>
        </div>
        <p className="mt-8 text-sm text-white/50">
          Need help?{' '}
          <a href="mailto:support@advoltix.com" className="text-accent-400 hover:text-accent-300 underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
